/**
 * Утилиты для работы с многоязычным контентом
 * Utilities for working with multilingual content
 */

/**
 * Получает контент на нужном языке из многоязычного поста
 * Gets content in the specified language from a multilingual post
 * 
 * @param {Object} post - Объект поста из Sanity
 * @param {string} [language='en'] - Код языка ('ru' или 'en')
 * @returns {Object} Объект с контентом на выбранном языке
 */
export function getLocalizedContent(post, language = 'en') {
    if (!post) return null;

    // Если статья моноязычная, всегда используем русский
    if (post.isMonolingual) {
        return {
            title: post.titleRu,
            description: post.descriptionRu,
            body: post.bodyRu,
            sections: post.sectionsRu || [],
            sources: post.sourcesRu || [],
            quickSummary: post.quickSummaryRu || [],
            tags: post.tags || [],
            slug: post.slug,
            author: post.author,
            mainImage: post.mainImage,
            categories: post.categories,
            publishedAt: post.publishedAt,
            hasRussian: true,
            hasEnglish: false,
            availableLanguages: ['ru'],
            currentLanguage: 'ru',
            actualLanguage: 'ru',
            isFallback: false
        };
    }

    const isRussian = language === 'ru';

    // Проверяем есть ли контент на запрашиваемом языке
    const hasRequestedLanguage = isRussian ? Boolean(post.titleRu) : Boolean(post.titleEn);
    const hasFallbackLanguage = isRussian ? Boolean(post.titleEn) : Boolean(post.titleRu);

    // Если нет контента на запрашиваемом языке, используем fallback
    const useFallback = !hasRequestedLanguage && hasFallbackLanguage;
    const contentLanguage = useFallback ? (isRussian ? 'en' : 'ru') : language;
    const useRussian = contentLanguage === 'ru';

    return {
        title: useRussian ? post.titleRu : post.titleEn,
        description: useRussian ? post.descriptionRu : post.descriptionEn,
        body: useRussian ? post.bodyRu : post.bodyEn,
        sections: useRussian ? (post.sectionsRu || []) : (post.sectionsEn || []),
        sources: useRussian ? (post.sourcesRu || []) : (post.sourcesEn || []),
        quickSummary: useRussian ? (post.quickSummaryRu || []) : (post.quickSummaryEn || []),
        tags: post.tags || [],
        // Общие поля остаются без изменений
        slug: post.slug,
        author: post.author,
        mainImage: post.mainImage,
        categories: post.categories,
        publishedAt: post.publishedAt,
        // Дополнительные поля для проверки доступности языков
        hasRussian: Boolean(post.titleRu),
        hasEnglish: Boolean(post.titleEn),
        availableLanguages: [
            post.titleRu && 'ru',
            post.titleEn && 'en'
        ].filter(Boolean),
        currentLanguage: language,
        actualLanguage: contentLanguage, // Фактический язык контента (может отличаться от запрашиваемого)
        isFallback: useFallback // Флаг использования fallback языка
    };
}

/**
 * Проверяет, доступен ли контент на указанном языке
 * Checks if content is available in the specified language
 * 
 * @param {Object} post - Объект поста из Sanity
 * @param {string} language - Код языка ('ru' или 'en')
 * @returns {boolean} True если контент доступен на этом языке
 */
export function hasLanguage(post, language) {
    if (!post) return false;

    // Моноязычные статьи доступны только на русском
    if (post.isMonolingual) {
        return language === 'ru';
    }

    if (language === 'ru') {
        return Boolean(post.titleRu);
    }

    if (language === 'en') {
        return Boolean(post.titleEn);
    }

    return false;
}

/**
 * Получает список доступных языков для поста
 * Gets list of available languages for a post
 * 
 * @param {Object} post - Объект поста из Sanity
 * @returns {string[]} Массив кодов доступных языков
 */
export function getAvailableLanguages(post) {
    if (!post) return [];

    // Моноязычные статьи доступны только на русском
    if (post.isMonolingual) {
        return ['ru'];
    }

    const languages = [];

    // EN-first: английский язык идет первым в списке
    if (post.titleEn) languages.push('en');
    if (post.titleRu) languages.push('ru');

    return languages;
}

/**
 * Получает альтернативный язык (если текущий недоступен)
 * Gets alternative language (if current is not available)
 * 
 * @param {Object} post - Объект поста из Sanity
 * @param {string} preferredLanguage - Предпочитаемый язык
 * @returns {string|null} Код доступного языка или null
 */
export function getFallbackLanguage(post, preferredLanguage = 'en') {
    if (!post) return null;

    // Для моноязычных статей всегда возвращаем русский
    if (post.isMonolingual) {
        return 'ru';
    }

    const available = getAvailableLanguages(post);

    // Если предпочитаемый язык доступен, возвращаем его
    if (available.includes(preferredLanguage)) {
        return preferredLanguage;
    }

    // Иначе возвращаем первый доступный
    return available[0] || null;
}

/**
 * Создает переключатель языков для многоязычного поста
 * Creates language switcher data for multilingual post
 * 
 * @param {Object} post - Объект поста из Sanity
 * @param {string} currentLanguage - Текущий язык
 * @param {string} baseUrl - Базовый URL (например, '/blog/')
 * @returns {Array} Массив объектов для переключателя языков
 */
export function createLanguageSwitcher(post, currentLanguage, baseUrl = '/blog/') {
    if (!post || !post.slug?.current) return [];

    // Для моноязычных статей не показываем переключатель
    if (post.isMonolingual) {
        return [];
    }

    const available = getAvailableLanguages(post);

    return available.map(lang => ({
        code: lang,
        label: lang === 'ru' ? 'Русский' : 'English',
        isActive: lang === currentLanguage,
        url: lang === 'ru'
            ? `${baseUrl}${post.slug.current}`
            : `${baseUrl}en/${post.slug.current}`,
        available: true
    }));
}

// createPostMetadata удалён 2026-08-13 (аудит §4.6): мёртвый код, строил
// несуществующие маршруты /blog/{slug} без языкового префикса. Реальные метаданные
// поста собирает generateBlogPostMetadata в src/lib/seo.js.
